import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Play, Heart, Target, Eye } from 'lucide-react';
import { motion } from 'framer-motion';
import { channels } from '../data/channels';

const AboutPage = () => {
  const milestones = [
    {
      year: "2022",
      title: "VR Media Network Founded",
      description: "Established to unify and manage multiple content verticals under one creative and strategic media brand."
    },
    {
      year: "January 2022",
      title: "VR Kitchen Channel Launch",
      description: "Launched to share authentic Indian recipes, home cooking tutorials, and culinary creativity with a broad audience."
    },
    {
      year: "March 2022",
      title: "VR Religious Channel Launch",
      description: "Started providing spiritual content including bhajans, mantras, rituals, and cultural storytelling to connect viewers with Indian traditions."
    },
    {
      year: "September 2022",
      title: "VR Short Stories Channel Launch",
      description: "Began engaging audiences through animated and real-life stories that entertain, educate, and inspire."
    },
    {
      year: "June 2023",
      title: "VR Wellness Channel Launch",
      description: "Launched to promote holistic health and wellness through tips on yoga, lifestyle, mindfulness, and personal growth."
    },
    {
      year: "2024",
      title: "Growing Community",
      description: "Reached thousands of subscribers across all channels, building meaningful communities and expanding our content reach."
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Helmet>
        <title>About VR Media Network - Our Story, Mission & Vision</title>
        <meta name="description" content="Learn about VR Media Network's mission to inspire, inform, and entertain through diverse YouTube channels. Discover our journey from culinary content to spiritual guidance." />
        <meta name="keywords" content="VR Media Network, about us, mission, vision, YouTube channels, digital content, Indian content creators" />
        <link rel="canonical" href="https://vrmedianetwork.com/about" />
        
        {/* Open Graph */}
        <meta property="og:title" content="About VR Media Network - Our Story, Mission & Vision" />
        <meta property="og:description" content="Learn about VR Media Network's mission to inspire, inform, and entertain through diverse YouTube channels." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://vrmedianetwork.com/about" />
        <meta property="og:image" content="https://vrmedianetwork.com/about-og-image.jpg" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About VR Media Network - Our Story, Mission & Vision" />
        <meta name="twitter:description" content="Learn about VR Media Network's mission to inspire, inform, and entertain through diverse YouTube channels." />
        <meta name="twitter:image" content="https://vrmedianetwork.com/about-twitter-image.jpg" />
      </Helmet>

      {/* Hero Section */}
      <motion.section 
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative py-20 bg-gradient-to-br from-vr-dark via-vr-darker to-vr-dark"
      >
        <div className="container-custom">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold font-display text-white mb-6">
              About VR Media Network
            </h1>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              A multi-channel digital content brand dedicated to educating, entertaining, and enriching lives through high-quality, niche-focused YouTube channels.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Main Content */}
      <section className="section-padding bg-vr-darker">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold font-display text-white mb-6">
                Our Story
              </h2>
              <p className="text-gray-300 leading-relaxed mb-6">
                Built on a foundation of creativity and purpose, VR Media Network curates content that resonates across generations — from culinary enthusiasts to wellness seekers, spiritual viewers to short story lovers.
              </p>
              <p className="text-gray-300 leading-relaxed">
                We believe in the power of authentic storytelling and meaningful content that not only entertains but also educates and inspires. Our journey began with a simple vision: to create content that makes a difference in people's lives.
              </p>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-vr-red/20 to-vr-blue/20 rounded-2xl p-8">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-vr-dark/50 rounded-lg">
                    <Users className="w-8 h-8 text-vr-red mx-auto mb-2" />
                    <div className="text-2xl font-bold text-white">5</div>
                    <div className="text-sm text-gray-400">Channels</div>
                  </div>
                  <div className="text-center p-4 bg-vr-dark/50 rounded-lg">
                    <Play className="w-8 h-8 text-vr-blue mx-auto mb-2" />
                    <div className="text-2xl font-bold text-white">500+</div>
                    <div className="text-sm text-gray-400">Videos</div>
                  </div>
                  <div className="text-center p-4 bg-vr-dark/50 rounded-lg">
                    <Heart className="w-8 h-8 text-vr-pink mx-auto mb-2" />
                    <div className="text-2xl font-bold text-white">6K+</div>
                    <div className="text-sm text-gray-400">Subscribers</div>
                  </div>
                  <div className="text-center p-4 bg-vr-dark/50 rounded-lg">
                    <Eye className="w-8 h-8 text-vr-yellow mx-auto mb-2" />
                    <div className="text-2xl font-bold text-white">100K+</div>
                    <div className="text-sm text-gray-400">Views</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mission & Vision */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="bg-vr-dark rounded-xl p-8 border border-gray-800">
              <div className="flex items-center mb-6">
                <Target className="w-8 h-8 text-vr-red mr-3" />
                <h3 className="text-2xl font-bold text-white">🎯 Mission</h3>
              </div>
              <p className="text-gray-300 leading-relaxed">
                To inspire, inform, and entertain audiences worldwide by delivering engaging content across diverse genres — consistently, creatively, and with cultural relevance.
              </p>
            </div>
            
            <div className="bg-vr-dark rounded-xl p-8 border border-gray-800">
              <div className="flex items-center mb-6">
                <Eye className="w-8 h-8 text-vr-blue mr-3" />
                <h3 className="text-2xl font-bold text-white">👁️ Vision</h3>
              </div>
              <p className="text-gray-300 leading-relaxed">
                To become a leading Indian digital content network by nurturing authentic creators, building meaningful communities, and producing content that makes a difference.
              </p>
            </div>
          </div>

                     {/* Our Channels Section */}
           <div className="mb-16">
             <h2 className="text-3xl md:text-4xl font-bold font-display text-white mb-8 text-center">
               🔗 Our Channels
             </h2>
             <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
               {channels.slice(1).map((channel) => {
                 // Custom descriptions based on the user's specifications
                 const customDescriptions = {
                   'VR Religious': 'To provide spiritual content including bhajans, mantras, rituals, and cultural storytelling to connect viewers with Indian traditions.',
                   'VR Kitchen': 'To share authentic Indian recipes, home cooking tutorials, and culinary creativity with a broad audience.',
                   'VR Short Stories': 'To engage audiences through animated and real-life stories that entertain, educate, and inspire.',
                   'VR Wellness': 'To promote holistic health and wellness through tips on yoga, lifestyle, mindfulness, and personal growth.'
                 };
                 
                 return (
                   <div key={channel.id} className="bg-vr-dark rounded-xl p-6 border border-gray-800 hover:border-gray-700 transition-colors">
                     <div className="flex items-center mb-4">
                       <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-800 flex items-center justify-center shadow-md mr-4">
                         <img 
                           src={channel.logo} 
                           alt={`${channel.name} logo`}
                           className="w-full h-full object-cover"
                           onError={(e) => {
                             e.target.style.display = 'none';
                             e.target.nextSibling.style.display = 'flex';
                           }}
                         />
                         <div className="w-full h-full bg-gray-700 flex items-center justify-center text-white text-sm font-bold" style={{display: 'none'}}>
                           {channel.name.split(' ').map(word => word[0]).join('')}
                         </div>
                       </div>
                       <div>
                         <h3 className="text-lg font-bold text-white">{channel.name}</h3>
                         <p className="text-sm text-gray-400">{channel.subscribers} subscribers</p>
                       </div>
                     </div>
                     <p className="text-gray-300 text-sm leading-relaxed mb-4">
                       {customDescriptions[channel.name] || channel.description}
                     </p>
                     <a
                       href={channel.youtubeUrl}
                       target="_blank"
                       rel="noopener noreferrer"
                       className={`inline-flex items-center text-sm font-medium transition-colors ${
                         channel.accentColor === 'vr-red' ? 'text-vr-red hover:text-red-400' :
                         channel.accentColor === 'vr-yellow' ? 'text-vr-yellow hover:text-yellow-400' :
                         channel.accentColor === 'vr-pink' ? 'text-vr-pink hover:text-pink-400' :
                         'text-vr-blue hover:text-blue-400'
                       }`}
                     >
                       Visit Channel
                       <ArrowRight size={16} className="ml-1" />
                     </a>
                   </div>
                 );
               })}
             </div>
           </div>

          {/* Timeline/Milestones */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-display text-white mb-8 text-center">
              Our Journey
            </h2>
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gray-700 transform md:-translate-x-1/2"></div>
              
              <div className="space-y-8">
                {milestones.map((milestone, index) => (
                  <div key={index} className={`relative flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    {/* Timeline dot */}
                    <div className="absolute left-4 md:left-1/2 w-4 h-4 bg-vr-red rounded-full transform md:-translate-x-1/2 z-10"></div>
                    
                    {/* Content */}
                    <div className={`ml-12 md:ml-0 md:w-5/12 ${index % 2 === 0 ? 'md:mr-auto md:pr-8' : 'md:ml-auto md:pl-8'}`}>
                      <div className="bg-vr-dark rounded-xl p-6 border border-gray-800">
                        <div className="text-vr-red font-bold text-lg mb-2">{milestone.year}</div>
                        <h3 className="text-xl font-bold text-white mb-2">{milestone.title}</h3>
                        <p className="text-gray-300 text-sm leading-relaxed">{milestone.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Values */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-display text-white mb-8 text-center">
              Our Values
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-vr-red/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-vr-red" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Authenticity</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  We believe in genuine storytelling and authentic content that resonates with our audience.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-vr-blue/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-vr-blue" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Community</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Building meaningful connections and fostering engaged communities across all our channels.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-vr-yellow/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-vr-yellow" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Purpose</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Every piece of content we create serves a purpose - to educate, entertain, or inspire.
                </p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold font-display text-white mb-6">
              Join Our Growing Community
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Be part of our journey as we continue to create meaningful content that makes a difference.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/channels" className="btn-primary inline-flex items-center space-x-2">
                <span>Explore Our Channels</span>
                <ArrowRight size={20} />
              </Link>
              <Link to="/contact" className="btn-secondary inline-flex items-center space-x-2">
                <span>Get In Touch</span>
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default AboutPage;
